"use client";

import React, { useEffect, useState } from "react";
import { Mail, MapPin, Building2, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const inputBaseClasses = "flex w-full min-w-0 rounded-sm border border-border bg-secondary/50 px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive placeholder:text-muted-foreground";
function PartnerForm({
  onReset
}: {
  onReset: () => void;
}) {
  const formState = useWvcForm();
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState(false);
  const {
    isSubmitting: sdkSubmitting,
    isSubmitted: sdkSubmitted,
    submitError: sdkSubmitError,
    successMessage,
    handleSubmit: sdkHandleSubmit,
    resetForm,
    control,
    setValue,
    getValues,
    errors
  } = formState as any;
  const isSubmitting = sdkSubmitting || localSubmitting;
  const isSubmitted = sdkSubmitted || localSuccess;
  const submitError = sdkSubmitError || localError;

  // Purge complète du stockage
  const handleFullReset = () => {
    if (typeof window !== "undefined") {
      Object.keys(localStorage).forEach(key => {
        if (key.includes("parlons_football") || key.includes("wvc") || key.includes("form")) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes("parlons_football") || key.includes("wvc") || key.includes("form")) {
          sessionStorage.removeItem(key);
        }
      });
    }
    if (typeof resetForm === "function") {
      resetForm();
    }
    setLocalSuccess(false);
    setLocalError(null);
    onReset();
  };
  if (isSubmitted && !submitError) {
    return <div data-section-id="223576" className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-primary/10 border border-primary/20 rounded-sm">
        <CircleCheck className="size-12 text-primary" />
        <div className="space-y-2">
          <h3 className="text-xl font-default font-bold text-foreground uppercase tracking-tight">Message Envoyé</h3>
          <p className="text-muted-foreground">{successMessage || "Votre demande a été transmise avec succès."}</p>
        </div>
        <Button onClick={handleFullReset} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Envoyer un autre message
        </Button>
      </div>;
  }

  // Soumission déclenchant l'API à coup sûr
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setLocalSubmitting(true);
    const values = typeof getValues === "function" ? getValues() : {};
    console.warn(" [DEBUG Submit] Lancement de la soumission. Payload :", values);
    try {
      // 1. Tenter d'exécuter la fonction du SDK WVC si elle est disponible
      let sdkSuccess = false;
      if (typeof sdkHandleSubmit === "function") {
        console.warn(" [DEBUG API] Exécution de sdkHandleSubmit...");
        try {
          await sdkHandleSubmit(e);
          sdkSuccess = true;
        } catch (sdkErr) {
          console.warn(" [DEBUG SDK] Le SDK a échoué, passage au fallback HTTP direct.", sdkErr);
        }
      }

      // 2. Si le SDK n'a pas validé/soumis la requête, on effectue l'appel REST direct
      if (!sdkSuccess) {
        const targetEmail = values.email_professionnel || values.email || values["email-professionnel"];

        // Payload normalisé avec toutes les variantes de clés pour WVC
        const payload = {
          form_id: "parlons_football_form_contact",
          form_key: "parlons_football_form_contact",
          data: {
            club_name: values.club_name,
            coach_name: values.coach_name,
            email: values.email_professionnel,
            // UN SEUL CHAMP EMAIL DANS DATA
            message: values.message
          }
        };
        console.warn(" [DEBUG API Fetch] Envoi de la requête HTTP directe...");
        const res = await fetch("/wp-json/wvc/v1/forms/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify(payload)
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false) {
          throw new Error(json.message || json.code || `Erreur serveur (${res.status})`);
        }
      }
      setLocalSuccess(true);
    } catch (err: any) {
      console.error(" [DEBUG Error] Échec de l'envoi API :", err);
      setLocalError(err.message || "Une erreur est survenue lors de l'envoi de votre message.");
    } finally {
      setLocalSubmitting(false);
    }
  };
  return <form onSubmit={onSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="club_name" rules={{
        required: "Le nom du club est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Nom du Club</FormLabel>
              <FormControl>
                <Input placeholder="Entrer ici le nom officiel du club" {...field} className="bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/30" />
              </FormControl>
              <FormDescription className="sr-only">Entrez le nom officiel de votre club.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="coach_name" rules={{
        required: "Le nom du coach est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Nom du Coach</FormLabel>
              <FormControl>
                <Input placeholder="Jean Dupont" {...field} className="bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/30" />
              </FormControl>
              <FormDescription className="sr-only">Entrez votre nom complet.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email_professionnel" rules={{
      required: "L'adresse email est requise",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Veuillez entrer une adresse email valide"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="coach@club.com" {...field} onChange={e => field.onChange(e.target.value)} className="bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/30" />
            </FormControl>
            <FormDescription className="sr-only">Nous vous contacterons via cette adresse.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Votre question est requise"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Votre Question</FormLabel>
            <FormControl>
              <textarea className={`${inputBaseClasses} min-h-[120px] py-2 resize-y bg-secondary/50 border-border focus-visible:border-primary focus-visible:ring-primary/30`} placeholder="Détaillez votre demande ou question ici..." {...field} />
            </FormControl>
            <FormDescription className="sr-only">Précisez l'objet de votre demande.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start gap-3 text-destructive">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} onClick={() => {
      if (errors && Object.keys(errors).length > 0) {
        console.error(" [DEBUG Validation] Erreurs sur les champs :", errors);
      }
    }} className="w-full h-12 text-base font-default font-bold uppercase tracking-widest bg-gradient-to-r from-primary to-chart-4 hover:opacity-90 transition-all animate-pulse shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Envoi en cours...
          </> : 'Envoyer le Message'}
      </Button>
    </form>;
}
export default function PartnerWithJadav() {
  const [formKey, setFormKey] = useState(0);
  return <section id="parlons-football" className="bg-background text-foreground py-24 px-6 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-start relative z-10">
        
        <div className="space-y-10 lg:sticky lg:top-24">
          <div className="space-y-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold tracking-tighter text-foreground uppercase">
              Parlons <span className="text-primary">Football</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-md font-default">
              Prêt à faire passer votre équipe au niveau supérieur ? Contactez l'élite de l'organisation de tournois pour une expérience mémorable.
            </p>
          </div>

          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="size-12 rounded-sm bg-secondary flex items-center justify-center shrink-0 border border-primary/30">
                <Building2 className="size-6 text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-default font-bold text-foreground mb-1 uppercase tracking-tight">Kickoff Events</h4>
                <p className="text-muted-foreground">L'excellence au service du football.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="size-12 rounded-sm bg-secondary flex items-center justify-center shrink-0 border border-primary/30">
                <Mail className="size-6 text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-default font-bold text-foreground mb-1 uppercase tracking-tight">Contact Direct</h4>
                <p className="text-muted-foreground">asso.kickoffevents@gmail.com</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="size-12 rounded-sm bg-secondary flex items-center justify-center shrink-0 border border-primary/30">
                <MapPin className="size-6 text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-default font-bold text-foreground mb-1 uppercase tracking-tight">Siège Social</h4>
                <p className="text-muted-foreground">Brax (31490), France</p>
              </div>
            </div>
          </div>
        </div>

        <Card className="border-primary/20 shadow-4xl shadow-black/60 bg-card/80 backdrop-blur-md rounded-sm">
          <CardHeader className="pb-8 border-b border-border/50">
            <CardTitle className="text-2xl font-default font-bold uppercase tracking-tight">Formulaire de Contact</CardTitle>
            <CardDescription className="text-base font-default">
              Remplissez les informations ci-dessous pour inscrire votre club ou poser une question.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <FormProvider key={formKey} formKey="parlons_football_form_contact" formId="parlons_football_form_contact" sectionName="Parlons Football" defaultValues={{
            club_name: "",
            coach_name: "",
            email: "",
            email_professionnel: "",
            "email-professionnel": "",
            message: ""
          }} submitText="Envoyer le Message" successMessage="Votre demande a été transmise avec succès. Notre équipe reviendra vers vous dans les plus brefs délais pour préparer votre prochain tournoi.">
              <PartnerForm onReset={() => setFormKey(k => k + 1)} />
            </FormProvider>
          </CardContent>
        </Card>

      </div>
    </section>;
}